/* ============================================================================
   Shared data hook for notifications — used by both the bell dropdown and
   the full Notifications list page so they stay in sync on refetch.
   ============================================================================ */

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPut, isApiError } from '../../lib/api';

export interface NotificationRow {
  id: number;
  type: string;
  title: string;
  body: string | null;
  isRead: boolean;
  relatedRequestId: number | null;
  createdAt: string;
}

export function useNotifications() {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet<NotificationRow[]>('/api/notifications');
      setRows(data);
      setError(null);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markRead = useCallback(async (id: number) => {
    // Optimistic update — the bell/list should feel instant.
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isRead: true } : r)));
    try {
      await apiPut(`/api/notifications/${id}/read`);
    } catch {
      // Revert on failure.
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isRead: false } : r)));
    }
  }, []);

  const unreadCount = rows.filter((r) => !r.isRead).length;

  return { rows, loading, error, unreadCount, refresh, markRead };
}
